import { useMediaQuery, useTheme } from "@mui/material";
import React, { useImperativeHandle, useState } from "react";
import { useTranslation } from "react-i18next";
import { Modal } from "@rentablo/components";
import { AccountAddSelection } from "./components/addChoiceBlock/AddChoiceBlock";
import { AddManuallyBlock } from "./components/addManuallyBlock/AddManuallyBlock";
import { BankImportBlock } from "./components/bankImportBlock/BankImportBlock";
import { ImportType, ImportTypeRef, ImportTypeText } from "./components/types";

const modalText: ImportTypeText = {
  "": {
    title: "accounts.add.title",
    subtitle: "accounts.add.subtitle",
  },
  bank: {
    // The subtitle is rendered per step (bank vs. BIC) by BankImportBlock.
    title: "accounts.add.titleBank",
  },
  manually: {
    title: "accounts.add.titleManual",
  },
};

interface AddModalProps {
  isOpen: boolean;
  setIsOpen: (isOpen: boolean) => void;
}

export const AddModal = ({
  ref,
  isOpen,
  setIsOpen,
}: AddModalProps & { ref?: React.Ref<ImportTypeRef> }) => {
  const theme = useTheme();
  const { t } = useTranslation();
  const [importType, setImportType] = useState<ImportType>({ type: "" });
  const isLessSm = useMediaQuery(theme.breakpoints.down("sm"));

  useImperativeHandle(ref, () => ({ setImportType }));

  const closeModal = () => {
    setIsOpen(false);
    setImportType({ type: "" });
  };

  return (
    <Modal
      isOpen={isOpen}
      title={t(modalText[importType.type].title)}
      subtitle={t(modalText[importType.type]?.subtitle || "")}
      titleVariant={isLessSm ? "h6" : "h4"}
      subtitleVariant={isLessSm ? "body3" : "body1"}
      width={importType.type === "bank" ? 768 : undefined}
      contentPadding={isLessSm ? theme.spacing(4) : theme.spacing(4, 8, 8)}
      height="fit-content"
      maxHeight={isLessSm ? "calc(100% - 60px)" : "auto"}
      position={isLessSm ? "bottom" : "centered"}
      onClose={closeModal}
    >
      {importType.type === "" && (
        <AccountAddSelection
          onSelection={setImportType}
          importType={importType}
        />
      )}
      {importType.type === "manually" && (
        <AddManuallyBlock
          closeModal={closeModal}
          onBack={() => setImportType({ type: "" })}
          defaultType={importType.default}
        />
      )}
      {importType.type === "bank" && (
        <BankImportBlock onBack={() => setImportType({ type: "" })} />
      )}
    </Modal>
  );
};

export default AddModal;
